import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import {
  Body,
  Box,
  CrossLightIcon,
  DrawerBottomSheet,
  Flex,
  Heading,
  Link,
  useDrawer,
} from '@edreamsodigeo/prisma-design-system';
import React from 'react';
import {
  MoreInfoBannerParag1SubtitleValuePropositionV2,
  NeedAppSubtitleBoldStyle,
} from './MoreInfoBanner.utils';
import {
  createFeature,
  Trans,
  useBrand,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import { PRIME_FIRST_PHASE_2_MOBILE } from '@/versionedComponents';
import { PriceStyle } from '../PriceStyle';
import { brandNames } from '@/constants/brandNames';
import { withNativeAware } from '@/HOC/withNativeAware';

export const MoreInfoDrawerBanner = ({
  days,
  cap,
  deposit,
  closeContent = () => {},
  openDrawer,
}) => {
  const { track } = usePFTracking();
  const { state, transitionDuration } = useDrawer({
    isOpen: openDrawer,
    transitionDuration: 300,
  });

  const closeHandler = () => {
    track(ACTIONS.LEARN_MORE.ONCLOSE);
    closeContent();
  };

  return (
    <DrawerBottomSheet state={state} transitionDuration={transitionDuration}>
      <MoreInfoDrawerHeader closeContent={closeHandler} />
      <MoreInfoBannerMobile cap={cap} deposit={deposit} days={days} />
    </DrawerBottomSheet>
  );
};

const MoreInfoDrawerHeader = ({ closeContent = () => {} }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box py="big" px="base" position="relative">
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <Box position="absolute" sx={{ left: '16px' }}>
            <CrossLightIcon
              size="medium"
              color="primary.50"
              cursor="pointer"
              onClick={closeContent}
              aria-label="closeIcon"
            />
          </Box>
          <Box flexGrow={3}>
            <Heading
              textAlign="center"
              type="4"
              weight="medium"
              color="neutral.90"
            >
              {t('pricefreeze.moreinfo.header.title')}
            </Heading>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const MoreInfoBannerNeedAppParag = ({ days }) => {
  const { t } = useTranslation();
  return (
    <Box pb="base">
      <Box pb="1x_small">
        <Heading type="5" weight="medium" color="neutral.90">
          {t('pricefreeze.moreinfo.needapp.title')}
        </Heading>
      </Box>
      <Box pb="2x_small">
        <Body type="small" weight="regular" color="neutral.90">
          <Trans
            i18nKey="pricefreeze.moreinfo.needapp.trip.subtitle.normal"
            components={{
              bold: (
                <NeedAppSubtitleBoldStyle
                  type="small"
                  content={t('pricefreeze.moreinfo.needapp.subtitle.bold')}
                />
              ),
              days: `${days}`,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerValuePropositionV2 = ({ days }) => {
  const { t } = useTranslation();
  return (
    <Box pb="base">
      <Box pb="1x_small">
        <Heading type="5" weight="medium" color="neutral.90">
          {t('moreinfo.needapp.valueprop.title', {})}
        </Heading>
      </Box>
      <Box pb="2x_small">
        <Body type="small" weight="regular" color="neutral.90">
          <Trans
            i18nKey="moreinfo.needapp.valueprop.normal"
            components={{
              bold: (
                <NeedAppSubtitleBoldStyle
                  t={t}
                  type="small"
                  content={t('moreinfo.needapp.valueprop.bold')}
                />
              ),
              days: `${days}`,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoTitle = ({ children }) => {
  return (
    <Box pb="1x_small">
      <Heading type="5" weight="medium" color="neutral.90">
        {children}
      </Heading>
    </Box>
  );
};

const MoreInfoSubtitle = ({ children }) => {
  return (
    <Box pb="2x_small">
      <Body type="small" weight="regular" color="neutral.90">
        {children}
      </Body>
    </Box>
  );
};

const MoreInfoBannerParag1 = ({ days, cap, deposit }) => {
  return (
    <Box pb="base">
      <MoreInfoTitle>
        <Trans
          i18nKey="pricefreeze.moreinfo.coverage.title"
          components={{
            price: <PriceStyle money={cap} rounded />,
          }}
        />
      </MoreInfoTitle>
      <Box pb="2x_small">
        <Body type="small" weight="regular" color="neutral.90">
          <MoreInfoBannerParag1SubtitleValuePropositionV2
            cap={cap}
            deposit={deposit}
            days={days}
          />
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerParag1PrimeFirst = ({ cap }) => {
  const brand = useBrand();
  const brandName = brandNames[brand];

  return (
    <Box pb="base">
      <MoreInfoTitle>
        <Trans
          i18nKey="pricefreeze.moreinfo.coverage.title"
          components={{
            price: <PriceStyle money={cap} rounded />,
          }}
        />
      </MoreInfoTitle>
      <MoreInfoSubtitle>
        <Trans
          i18nKey="pricefreeze.primefirst.moreinfo.coverage.subtitle"
          components={{
            brand: brandName,
            price: <PriceStyle money={cap} rounded />,
          }}
        />
      </MoreInfoSubtitle>
    </Box>
  );
};

const NativeMoreInfoBannerParag1Variation = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    MoreInfoBannerParag1,
    MoreInfoBannerParag1PrimeFirst,
    MoreInfoBannerParag1PrimeFirst,
  ],
  version: 1,
});

export const MoreInfoBannerParag1Variation = withNativeAware(
  NativeMoreInfoBannerParag1Variation,
  MoreInfoBannerParag1PrimeFirst
);

const MoreInfoBannerParag2 = ({ days }) => {
  return <MoreInfoBannerValuePropositionV2 days={days} />;
};

const MoreInfoBannerParag2PrimeFirst = () => {
  const { t } = useTranslation();

  return (
    <Box pb="base">
      <MoreInfoTitle>
        {t('pricefreeze.primefirst.moreinfo.book.title')}
      </MoreInfoTitle>
      <MoreInfoSubtitle>
        {t('pricefreeze.primefirst.moreinfo.book.subtitle')}
      </MoreInfoSubtitle>
    </Box>
  );
};

const NativeMoreInfoBannerParag2Variation = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    MoreInfoBannerParag2,
    MoreInfoBannerParag2PrimeFirst,
    MoreInfoBannerParag2PrimeFirst,
  ],
  version: 1,
});

export const MoreInfoBannerParag2Variation = withNativeAware(
  NativeMoreInfoBannerParag2Variation,
  MoreInfoBannerParag2PrimeFirst
);

const PrimeRepeatInfoDescription = ({ t }) =>
  t('moreinfo.subscription.valueprop.subtitle');
const MoreInfoBannerParag3 = () => {
  const { t } = useTranslation();
  const { track } = usePFTracking();

  return (
    <Box pb="1x_big">
      <MoreInfoTitle>
        {t('moreinfo.subscription.valueprop.title')}
      </MoreInfoTitle>
      <Box>
        <Body type="small" weight="regular" color="neutral.90" inline>
          <PrimeRepeatInfoDescription t={t} />{' '}
        </Body>
        <Body
          ml="big"
          type="xSmall"
          weight="regular"
          color="neutral.90"
          sx={{ whiteSpace: 'nowrap' }}
          inline
          onClick={() => track(ACTIONS.LEARN_MORE.CLICK.PRIME)}
        >
          <Link href={t('terms.prime.url')} target={'_blank'} type={'dotted'}>
            {t('pricefreeze.moreinfo.subscription.subtitle.link')}
          </Link>
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerParag3PrimeFirst = ({ days }) => {
  return <MoreInfoBannerNeedAppParag days={days} />;
};

const NativeMoreInfoBannerParag3Variation = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    MoreInfoBannerParag3,
    MoreInfoBannerParag3PrimeFirst,
    MoreInfoBannerParag3PrimeFirst,
  ],
  version: 1,
});

export const MoreInfoBannerParag3Variation = withNativeAware(
  NativeMoreInfoBannerParag3Variation,
  MoreInfoBannerParag3PrimeFirst
);

const FullTermsAndConditionsLink = () => {
  const { t } = useTranslation();
  const { track } = usePFTracking();
  return (
    <Box pb="1x_big" sx={{ textAlign: 'center' }}>
      <Body
        inline
        type="xSmall"
        weight="regular"
        textAlign="center"
        onClick={() => track(ACTIONS.LEARN_MORE.CLICK.TERMS)}
      >
        <Link
          href={t('terms.pricefreeze.url')}
          target={'_blank'}
          type={'dotted'}
        >
          {t('pricefreeze.moreinfo.termsandconditions.link')}
        </Link>
      </Body>
    </Box>
  );
};

export const MoreInfoBannerMobile = ({ days, cap, deposit }) => {
  return (
    <>
      <Box px="base" pt="base">
        {deposit.amount !== 0 ? (
          <MoreInfoBannerParag1Variation
            days={days}
            cap={cap}
            deposit={deposit}
          />
        ) : (
          <MoreInfoBannerParag1PrimeFirst cap={cap} />
        )}
        <MoreInfoBannerParag2Variation days={days} />
        <MoreInfoBannerParag3Variation days={days} />
        <FullTermsAndConditionsLink />
      </Box>
    </>
  );
};
